import { useEffect, useRef } from "react";

const DEFAULT_EDGE = 70;
const DEFAULT_MAX_SPEED = 16;
const MIN_SPEED = 2;

const isWindowScroller = (el) =>
  el === document.scrollingElement ||
  el === document.documentElement ||
  el === document.body;

// Nearest scrollable ancestor of `start`, else the document scroller. Resolved
// at drag time because most pages scroll the window, not an inner box.
const resolveScroller = (start) => {
  let el = start;
  while (el && el !== document.body) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 1) {
      return el;
    }
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

/**
 * Edge auto-scroll for native HTML5 drag-reorder lists. While `active`, a
 * pointer within `edge` px of the scroller's top/bottom scrolls it every
 * frame, ramping up to `maxSpeed` px/frame at the very edge, so an item can be
 * dragged past the visible part of a long list.
 *
 * `topOffset` is the height of any sticky app header covering the top of the
 * window; the top edge zone starts below it so it stays reachable.
 */
const useDragAutoScroll = (
  active,
  containerRef,
  { edge = DEFAULT_EDGE, maxSpeed = DEFAULT_MAX_SPEED, topOffset = 0 } = {},
) => {
  const stateRef = useRef({ raf: 0, dir: 0, speed: 0, el: null });

  useEffect(() => {
    if (!active) return undefined;
    const s = stateRef.current;

    const step = () => {
      if (!s.el || s.dir === 0) {
        s.raf = 0;
        return;
      }
      s.el.scrollTop += s.dir * s.speed;
      s.raf = requestAnimationFrame(step);
    };

    // Document-level so it fires whatever sits under the pointer (rows,
    // sticky headers, page chrome).
    const onDragOver = (e) => {
      const el = resolveScroller(containerRef.current);
      if (!el) return;
      const rect = isWindowScroller(el) ? null : el.getBoundingClientRect();
      const top = rect ? rect.top : topOffset;
      const bottom = rect ? rect.bottom : window.innerHeight;
      const fromTop = e.clientY - top;
      const fromBottom = bottom - e.clientY;
      s.el = el;
      if (fromTop < edge) {
        s.dir = -1;
        s.speed = Math.max(MIN_SPEED, ((edge - Math.max(fromTop, 0)) / edge) * maxSpeed);
      } else if (fromBottom < edge) {
        s.dir = 1;
        s.speed = Math.max(MIN_SPEED, ((edge - Math.max(fromBottom, 0)) / edge) * maxSpeed);
      } else {
        s.dir = 0;
        s.speed = 0;
      }
      if (s.dir !== 0 && !s.raf) s.raf = requestAnimationFrame(step);
    };

    document.addEventListener("dragover", onDragOver);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      if (s.raf) cancelAnimationFrame(s.raf);
      Object.assign(s, { raf: 0, dir: 0, speed: 0, el: null });
    };
  }, [active, containerRef, edge, maxSpeed, topOffset]);
};

export default useDragAutoScroll;
